<script setup lang="ts">
import { computed } from "vue";

import type { Component } from "vue";
import type { iconName, iconSize } from "@/Modules/App/Models/Styles/Icons";
import type { colorTag } from "@/Modules/App/Models/Styles/Colors";

import AddUserIcon from "@/Components/Icons/AddUser.vue";
import AlertDisabledIcon from "@/Components/Icons/AlertDisabled.vue";
import AlertIcon from "@/Components/Icons/Alert.vue";
import ArchiveIcon from "@/Components/Icons/Archive.vue";
import ArrowRightIcon from "@/Components/Icons/ArrowRight.vue";
import BackIcon from "@/Components/Icons/Back.vue";
import BlurIcon from "@/Components/Icons/Blur.vue";
import BotIcon from "@/Components/Icons/Bot.vue";
import BotDisabledIcon from "@/Components/Icons/BotDisabled.vue";
import CalendarIcon from "@/Components/Icons/Calendar.vue";
import CamActiveIcon from "@/Components/Icons/CamActive.vue";
import CameraSwapIcon from "@/Components/Icons/CameraSwap.vue";
import CamIcon from "@/Components/Icons/Cam.vue";
import CamInactiveIcon from "@/Components/Icons/CamInactive.vue";
import CardIcon from "@/Components/Icons/Card.vue";
import CardReassignedIcon from "@/Components/Icons/CardReassigned.vue";
import ChatEndIcon from "@/Components/Icons/ChatEnd.vue";
import ChartIcon from "@/Components/Icons/Chart.vue";
import ChatIcon from "@/Components/Icons/Chat.vue";
import CheckboxChecked from "@/Components/Icons/CheckboxChecked.vue";
import CheckboxEmpty from "@/Components/Icons/CheckboxEmpty.vue";
import CheckIcon from "@/Components/Icons/Check.vue";
import DocumentIcon from "@/Components/Icons/Document.vue";
import DoubleCheckIcon from "@/Components/Icons/DoubleCheck.vue";
import ChevronDownIcon from "@/Components/Icons/ChevronDown.vue";
import ChevronLeftIcon from "@/Components/Icons/ChevronLeft.vue";
import ChevronRightIcon from "@/Components/Icons/ChevronRight.vue";
import ChevronsLeftIcon from "@/Components/Icons/ChevronsLeft.vue";
import ChevronsRightIcon from "@/Components/Icons/ChevronsRight.vue";
import ChevronUpIcon from "@/Components/Icons/ChevronUp.vue";
import ClipboardCheckIcon from "@/Components/Icons/ClipboardCheck.vue";
import ClipIcon from "@/Components/Icons/Clip.vue";
import ClockIcon from "@/Components/Icons/Clock.vue";
import CloseCircleIcon from "@/Components/Icons/CloseCircle.vue";
import CopyIcon from "@/Components/Icons/Copy.vue";
import CrossIcon from "@/Components/Icons/Cross.vue";
import CountryEsIcon from "@/Components/Icons/CountryEs.vue";
import CountryEnIcon from "@/Components/Icons/CountryEn.vue";
import CountryItIcon from "@/Components/Icons/CountryIt.vue";
import CountryPtIcon from "@/Components/Icons/CountryPt.vue";
import DeleteCalendarIcon from "@/Components/Icons/DeleteCalendar.vue";
import DeleteIcon from "@/Components/Icons/Delete.vue";
import DownloadIcon from "@/Components/Icons/Download.vue";
import EditIcon from "@/Components/Icons/Edit.vue";
import EmailIcon from "@/Components/Icons/Email.vue";
import EmoticonsIcon from "@/Components/Icons/Emoticons.vue";
import ExpandIcon from "@/Components/Icons/Expand.vue";
import EyeOffIcon from "@/Components/Icons/EyeOff.vue";
import EyeOnIcon from "@/Components/Icons/EyeOn.vue";
import FacebookIcon from "@/Components/Icons/Facebook.vue";
import FilterIcon from "@/Components/Icons/Filter.vue";
import FormIcon from "@/Components/Icons/Form.vue";
import GenderUnspecifiedIcon from "@/Components/Icons/GenderUnspecified.vue";
import GendreManIcon from "@/Components/Icons/GendreMan.vue";
import GendreWomanIcon from "@/Components/Icons/GendreWoman.vue";
import GiftIcon from "@/Components/Icons/Gift.vue";
import HangCallIcon from "@/Components/Icons/HangCall.vue";
import HeadsetIcon from "@/Components/Icons/Headset.vue";
import InfoCircleIcon from "@/Components/Icons/InfoCircle.vue";
import InstagramIcon from "@/Components/Icons/Instagram.vue";
import LessIcon from "@/Components/Icons/Less.vue";
import LinkedinIcon from "@/Components/Icons/Linkedin.vue";
import LinkIcon from "@/Components/Icons/Link.vue";
import ListIcon from "@/Components/Icons/List.vue";
import LoadingIcon from "@/Components/Icons/Loading.vue";
import LockIcon from "@/Components/Icons/Lock.vue";
import LogOutIcon from "@/Components/Icons/LogOut.vue";
import OptionsIcon from "@/Components/Icons/Options.vue";
import PatientIcon from "@/Components/Icons/Patient.vue";
import PatientReassignedIcon from "@/Components/Icons/PatientReassigned.vue";
import PauseCircleIcon from "@/Components/Icons/PauseCircle.vue";
import PauseIcon from "@/Components/Icons/Pause.vue";
import PaymentErrorIcon from "@/Components/Icons/PaymentError.vue";
import PaymentMadeIcon from "@/Components/Icons/PaymentMade.vue";
import PlaceholderIcon from "@/Components/Icons/Placeholder.vue";
import PlayIcon from "@/Components/Icons/Play.vue";
import PlusIcon from "@/Components/Icons/Plus.vue";
import RefundIcon from "@/Components/Icons/Refund.vue";
import RejectedCallIcon from "@/Components/Icons/RejectedCall.vue";
import RibbonIcon from "@/Components/Icons/Ribbon.vue";
import SearchIcon from "@/Components/Icons/Search.vue";
import SendIcon from "@/Components/Icons/Send.vue";
import SettingsIcon from "@/Components/Icons/Settings.vue";
import ShareScreenActiveIcon from "@/Components/Icons/ShareScreenActive.vue";
import ShareScreenIcon from "@/Components/Icons/ShareScreen.vue";
import SortIcon from "@/Components/Icons/Sort.vue";
import TrialWeekIcon from "@/Components/Icons/TrialWeek.vue";
import TherapyIcon from "@/Components/Icons/Therapy.vue";
import UserIcon from "@/Components/Icons/User.vue";
import UsersIcon from "@/Components/Icons/Users.vue";
import VoiceMessageActiveIcon from "@/Components/Icons/VoiceMessageActive.vue";
import VoiceMessageIcon from "@/Components/Icons/VoiceMessage.vue";
import WarningCircleIcon from "@/Components/Icons/WarningCircle.vue";
import WhatsAppIcon from "@/Components/Icons/Whatsapp.vue";
import SuitcaseIcon from "@/Components/Icons/Suitcase.vue";
import CoupleIcon from "@/Components/Icons/Couple.vue";
import Under18Icon from "@/Components/Icons/Under18.vue";

import { hasValue } from "@/Utils/Objects";

export interface IconResolverProps {
  name: iconName;
  size?: iconSize;
  color?: colorTag;
  background?: colorTag;
}

const props = defineProps<IconResolverProps>();

const Icon = computed((): Component | undefined => {
  switch (props.name) {
    case "AddUser":
      return AddUserIcon;
    case "Alert":
      return AlertIcon;
    case "AlertDisabled":
      return AlertDisabledIcon;
    case "Archive":
      return ArchiveIcon;
    case "ArrowRight":
      return ArrowRightIcon;
    case "Back":
      return BackIcon;
    case "Blur":
      return BlurIcon;
    case "Bot":
      return BotIcon;
    case "BotDisabled":
      return BotDisabledIcon;
    case "Calendar":
      return CalendarIcon;
    case "Cam":
      return CamIcon;
    case "CamInactive":
      return CamInactiveIcon;
    case "CamActive":
      return CamActiveIcon;
    case "CheckboxEmpty":
      return CheckboxEmpty;
    case "CheckboxChecked":
      return CheckboxChecked;
    case "CameraSwap":
      return CameraSwapIcon;
    case "Card":
      return CardIcon;
    case "CardReassigned":
      return CardReassignedIcon;
    case "Clip":
      return ClipIcon;
    case "ClipboardCheck":
      return ClipboardCheckIcon;
    case "Clock":
      return ClockIcon;
    case "CloseCircle":
      return CloseCircleIcon;
    case "Copy":
      return CopyIcon;
    case "Cross":
      return CrossIcon;
    case "CountryEs":
      return CountryEsIcon;
    case "CountryIt":
      return CountryItIcon;
    case "CountryEn":
      return CountryEnIcon;
    case "CountryPt":
      return CountryPtIcon;
    case "Chart":
      return ChartIcon;
    case "Chat":
      return ChatIcon;
    case "ChatEnd":
      return ChatEndIcon;
    case "Check":
      return CheckIcon;
    case "Document":
      return DocumentIcon;
    case "DoubleCheck":
      return DoubleCheckIcon;
    case "ChevronDown":
      return ChevronDownIcon;
    case "ChevronLeft":
      return ChevronLeftIcon;
    case "ChevronRight":
      return ChevronRightIcon;
    case "ChevronUp":
      return ChevronUpIcon;
    case "ChevronsLeft":
      return ChevronsLeftIcon;
    case "ChevronsRight":
      return ChevronsRightIcon;
    case "Couple":
      return CoupleIcon;
    case "Delete":
      return DeleteIcon;
    case "DeleteCalendar":
      return DeleteCalendarIcon;
    case "Download":
      return DownloadIcon;
    case "Edit":
      return EditIcon;
    case "Email":
      return EmailIcon;
    case "Emoticons":
      return EmoticonsIcon;
    case "Expand":
      return ExpandIcon;
    case "EyeOff":
      return EyeOffIcon;
    case "EyeOn":
      return EyeOnIcon;
    case "Facebook":
      return FacebookIcon;
    case "Filter":
      return FilterIcon;
    case "Form":
      return FormIcon;
    case "GenderUnspecified":
      return GenderUnspecifiedIcon;
    case "GendreMan":
      return GendreManIcon;
    case "GendreWoman":
      return GendreWomanIcon;
    case "Gift":
      return GiftIcon;
    case "HangCall":
      return HangCallIcon;
    case "Headset":
      return HeadsetIcon;
    case "InfoCircle":
      return InfoCircleIcon;
    case "Instagram":
      return InstagramIcon;
    case "Less":
      return LessIcon;
    case "Link":
      return LinkIcon;
    case "Linkedin":
      return LinkedinIcon;
    case "List":
      return ListIcon;
    case "Loading":
      return LoadingIcon;
    case "Lock":
      return LockIcon;
    case "LogOut":
      return LogOutIcon;
    case "Options":
      return OptionsIcon;
    case "Patient":
      return PatientIcon;
    case "PatientReassigned":
      return PatientReassignedIcon;
    case "Pause":
      return PauseIcon;
    case "PauseCircle":
      return PauseCircleIcon;
    case "PaymentError":
      return PaymentErrorIcon;
    case "PaymentMade":
      return PaymentMadeIcon;
    case "Placeholder":
      return PlaceholderIcon;
    case "Play":
      return PlayIcon;
    case "Plus":
      return PlusIcon;
    case "Refund":
      return RefundIcon;
    case "RejectedCall":
      return RejectedCallIcon;
    case "Ribbon":
      return RibbonIcon;
    case "Search":
      return SearchIcon;
    case "Send":
      return SendIcon;
    case "Settings":
      return SettingsIcon;
    case "ShareScreen":
      return ShareScreenIcon;
    case "ShareScreenActive":
      return ShareScreenActiveIcon;
    case "Sort":
      return SortIcon;
    case "TrialWeek":
      return TrialWeekIcon;
    case "Therapy":
      return TherapyIcon;
    case "Under18":
      return Under18Icon;
    case "Users":
      return UsersIcon;
    case "User":
      return UserIcon;
    case "VoiceMessage":
      return VoiceMessageIcon;
    case "VoiceMessageActive":
      return VoiceMessageActiveIcon;
    case "WarningCircle":
      return WarningCircleIcon;
    case "WhatsApp":
      return WhatsAppIcon;
    case "Suitcase":
      return SuitcaseIcon;
    default:
      return undefined;
  }
});
</script>

<template>
  <component :is="Icon" v-bind="props" v-if="hasValue(Icon)" />
</template>

<style scoped lang="scss"></style>
